import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState } from '@ui-kit';
import LoadingSpinner from './components/LoadingSpinner';
import ServiceDashboard from './ServiceDashboard';
import type { StaffRolesMap } from './context/StaffRolesContext';

declare const __UI_KIT_SHOWCASE__: boolean;

const EcommerceService = lazy(() => import('../services/ecommerce/miniapp/EcommerceRoot'));
const EcommerceManager = lazy(() => import('../services/ecommerce/miniapp/EcommerceManagerRoot'));
const MarketplaceService = lazy(() => import('../services/marketplace/miniapp/MarketplaceRoot'));
const MarketplaceModerator = lazy(
  () => import('../services/marketplace/miniapp/MarketplaceModeratorRoot'),
);
const EventsService = lazy(() => import('../services/events/miniapp/EventsRoot'));
const EventsManager = lazy(() => import('../services/events/miniapp/EventsManagerRoot'));
const DigitalMediaService = lazy(
  () => import('../services/digital-media/miniapp/DigitalMediaRoot'),
);
const DigitalMediaManager = lazy(
  () => import('../services/digital-media/miniapp/DigitalMediaManagerRoot'),
);
const HealthBeautyService = lazy(
  () => import('../services/health-and-beauty/miniapp/HealthBeautyRoot'),
);
const HealthBeautyManager = lazy(
  () => import('../services/health-and-beauty/miniapp/HealthBeautyManagerRoot'),
);
const PetZone = lazy(() => import('../services/pet-zone/miniapp/PetZoneRoot'));
const PetZoneManager = lazy(() => import('../services/pet-zone/miniapp/PetZoneManagerRoot'));
const EducationService = lazy(() => import('../services/education/miniapp/EducationRoot'));
const EducationManager = lazy(() => import('../services/education/miniapp/EducationManagerRoot'));
const RestaurantsService = lazy(() => import('../services/restaurants/miniapp/RestaurantsRoot'));
const RestaurantsManager = lazy(
  () => import('../services/restaurants/miniapp/RestaurantsManagerRoot'),
);
const FreelanceServices = lazy(
  () => import('../services/freelance-services/miniapp/FreelanceServicesRoot'),
);
const FreelanceServicesManager = lazy(
  () => import('../services/freelance-services/miniapp/FreelanceServicesManagerRoot'),
);
const RealEstate = lazy(() => import('../services/real-estate/miniapp/RealEstateRoot'));
const RealEstateManager = lazy(
  () => import('../services/real-estate/miniapp/RealEstateManagerRoot'),
);
const UiKitShowcase = __UI_KIT_SHOWCASE__
  ? lazy(() => import('../core/miniapp/showcase/MiniappShowcase'))
  : null;

interface AppRoutesProps {
  activeServices: string[];
  staffRoles: StaffRolesMap;
  servicesLoading: boolean;
  servicesError: string | null;
}

interface ServiceRouteGateProps {
  serviceName: string;
  activeServices: string[];
  servicesLoading: boolean;
  servicesError: string | null;
  children: ReactNode;
}

interface ManagerRouteGateProps extends ServiceRouteGateProps {
  isManager: boolean;
}

function ServiceRouteGate({
  serviceName,
  activeServices,
  servicesLoading,
  servicesError,
  children,
}: ServiceRouteGateProps) {
  const { t } = useTranslation('shell');

  if (servicesLoading) {
    return <LoadingSpinner />;
  }

  if (servicesError) {
    return <ErrorState message={servicesError} onRetry={() => window.location.reload()} />;
  }

  if (!activeServices.includes(serviceName)) {
    return <EmptyState title={t('service.unavailable')} />;
  }

  return <>{children}</>;
}

function ManagerRouteGate({ isManager, ...props }: ManagerRouteGateProps) {
  const { t } = useTranslation('shell');

  return (
    <ServiceRouteGate {...props}>
      {isManager ? (
        props.children
      ) : (
        <EmptyState title={t('staff.badge')} description={t('dashboard.subtitle')} />
      )}
    </ServiceRouteGate>
  );
}

export function AppRoutes({
  activeServices,
  staffRoles,
  servicesLoading,
  servicesError,
}: AppRoutesProps) {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {UiKitShowcase ? <Route path="/ui-kit" element={<UiKitShowcase />} /> : null}
        <Route
          path="/"
          element={
            <ServiceDashboard
              activeServices={activeServices}
              staffRoles={staffRoles}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            />
          }
        />
        <Route
          path="/ecommerce/manager/*"
          element={
            <ServiceRouteGate
              serviceName="ecommerce"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <EcommerceManager />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/ecommerce/*"
          element={
            <ServiceRouteGate
              serviceName="ecommerce"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <EcommerceService />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/marketplace/moderator/*"
          element={
            <ServiceRouteGate
              serviceName="marketplace"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <MarketplaceModerator />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/marketplace/*"
          element={
            <ServiceRouteGate
              serviceName="marketplace"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <MarketplaceService />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/digital-media/manager/*"
          element={
            <ServiceRouteGate
              serviceName="digital-media"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <DigitalMediaManager />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/digital-media/*"
          element={
            <ServiceRouteGate
              serviceName="digital-media"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <DigitalMediaService />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/health-and-beauty/manager/*"
          element={
            <ServiceRouteGate
              serviceName="health-and-beauty"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <HealthBeautyManager />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/health-and-beauty/*"
          element={
            <ServiceRouteGate
              serviceName="health-and-beauty"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <HealthBeautyService />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/pet-zone/manager/*"
          element={
            <ManagerRouteGate
              serviceName="pet-zone"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
              isManager={Boolean(staffRoles['pet-zone']?.isManager)}
            >
              <PetZoneManager />
            </ManagerRouteGate>
          }
        />
        <Route
          path="/pet-zone/*"
          element={
            <ServiceRouteGate
              serviceName="pet-zone"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <PetZone />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/education/manager/*"
          element={
            <ServiceRouteGate
              serviceName="education"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <EducationManager />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/education/*"
          element={
            <ServiceRouteGate
              serviceName="education"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <EducationService />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/restaurants/manager/*"
          element={
            <ServiceRouteGate
              serviceName="restaurants"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <RestaurantsManager />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/restaurants/*"
          element={
            <ServiceRouteGate
              serviceName="restaurants"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <RestaurantsService />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/freelance-services/manager/*"
          element={
            <ServiceRouteGate
              serviceName="freelance-services"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <FreelanceServicesManager />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/freelance-services/*"
          element={
            <ServiceRouteGate
              serviceName="freelance-services"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <FreelanceServices />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/real-estate/manager/*"
          element={
            <ManagerRouteGate
              serviceName="real-estate"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
              isManager={Boolean(staffRoles['real-estate']?.isManager)}
            >
              <RealEstateManager />
            </ManagerRouteGate>
          }
        />
        <Route
          path="/real-estate/*"
          element={
            <ServiceRouteGate
              serviceName="real-estate"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <RealEstate />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/events/manager/*"
          element={
            <ServiceRouteGate
              serviceName="events"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <EventsManager />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/events/*"
          element={
            <ServiceRouteGate
              serviceName="events"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <EventsService />
            </ServiceRouteGate>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
